import type { MembershipPlan, MembershipTrialUnit } from './types';

const UNIT_SUFFIX: Record<MembershipTrialUnit, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
};

/**
 * Whether buying `plan` now starts with a free trial for this customer. Only a
 * plan from `GET /plans` can say so: the backend sets `trialEligible`
 * personally there (`false` once the customer had a membership) and always
 * `false` in `/me`, where a plan change never starts a trial.
 */
export const isTrialEligible = (
  plan: Pick<MembershipPlan, 'trialEligible' | 'trialLength' | 'trialUnit'>,
): boolean =>
  plan.trialEligible === true &&
  plan.trialLength > 0 &&
  Object.hasOwn(UNIT_SUFFIX, plan.trialUnit);

/**
 * `prefix` followed by the trial unit (`membership:clubStartTrial` →
 * `membership:clubStartTrialMonth`), the plural key a trial copy is read
 * from with `count` = the trial length.
 */
export const trialKey = (prefix: string, unit: MembershipTrialUnit): string =>
  `${prefix}${UNIT_SUFFIX[unit]}`;

/**
 * End of a trial started at `start`, as the backend computes it
 * (`addTrialPeriod`): days and weeks in UTC days, a month is a calendar month
 * clamped to the last day of the target month (31 Jan + 1 month = 28/29 Feb).
 */
export const trialEndOf = (
  start: Date,
  length: number,
  unit: MembershipTrialUnit,
): Date => {
  const end = new Date(start.getTime());
  if (unit === 'day' || unit === 'week') {
    end.setUTCDate(end.getUTCDate() + length * (unit === 'week' ? 7 : 1));
    return end;
  }
  const day = end.getUTCDate();
  end.setUTCDate(1);
  end.setUTCMonth(end.getUTCMonth() + length);
  const lastDay = new Date(
    Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 0),
  ).getUTCDate();
  end.setUTCDate(Math.min(day, lastDay));
  return end;
};
